// Claude: shared admin page for Seller Feedback and Buyer Feedback 26-03-2026
import React, { useEffect, useState } from "react";
import { Box, Button, Flex, Text, Tooltip } from "@chakra-ui/react";
import { Modal, Table } from "antd";
import { AdminAPI } from "../../Apis/adminAPI";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { errorHandler } from "../../Utils/handler";

type FeedbackEntry = {
  _id: string;
  personName: string;
  companyName: string;
  feedbackText: string;
  createdAt: string;
};

type Props = {
  role: "Seller" | "Buyer";
  title: string;
};

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const FeedbackPage = ({ role, title }: Props) => {
  const { success, error } = useToastWrapper();
  const [data, setData] = useState<FeedbackEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await AdminAPI.getFeedback(role);
      setData(res?.data ?? res ?? []);
    } catch (err: any) {
      error(errorHandler(err));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [role]);

  const handleDelete = (entry: FeedbackEntry) => {
    // Claude: frontend guard — warn before API call; backend also enforces 30-day rule 26-03-2026
    const age = Date.now() - new Date(entry.createdAt).getTime();
    if (age < THIRTY_DAYS_MS) {
      error("Feedback can only be deleted after 30 days.");
      return;
    }
    Modal.confirm({
      title: "Delete feedback?",
      content: `Feedback from ${entry.personName} (${entry.companyName}) will be permanently deleted.`,
      okText: "Yes, Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await AdminAPI.deleteFeedback(entry._id);
          success("Feedback deleted.");
          await load();
        } catch (err: any) {
          error(errorHandler(err));
        }
      },
    });
  };

  const columns = [
    {
      title: "Company",
      dataIndex: "companyName",
      width: 160,
      render: (text: string) => <Text fontSize="sm" fontWeight={500}>{text}</Text>,
    },
    {
      title: "Person",
      dataIndex: "personName",
      width: 140,
      render: (text: string) => <Text fontSize="sm">{text}</Text>,
    },
    {
      title: "Feedback",
      dataIndex: "feedbackText",
      render: (text: string) => <Text fontSize="sm" color="gray.700">{text}</Text>,
    },
    {
      title: "Timestamp",
      dataIndex: "createdAt",
      width: 180,
      render: (val: string) => (
        <Text fontSize="sm" color="gray.500">
          {new Date(val).toLocaleString()}
        </Text>
      ),
    },
    {
      title: "Actions",
      width: 100,
      render: (_: any, record: FeedbackEntry) => {
        const age = Date.now() - new Date(record.createdAt).getTime();
        const canDelete = age >= THIRTY_DAYS_MS;
        return (
          <Tooltip label={canDelete ? "" : "Deletable after 30 days"} hasArrow placement="top">
            <Button
              size="xs"
              bg="#C62828"
              color="white"
              _hover={{ bg: canDelete ? "#B71C1C" : "#C62828" }}
              opacity={canDelete ? 1 : 0.5}
              onClick={() => handleDelete(record)}
            >
              Delete
            </Button>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Box p={6}>
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Flex alignItems="baseline" gap={2}>
          <Text fontSize="xl" fontWeight={700}>{title}</Text>
          {!loading && (
            <Text fontSize="sm" color="gray.500">
              ({data.length} {data.length === 1 ? "entry" : "entries"})
            </Text>
          )}
        </Flex>
      </Flex>
      <Table
        dataSource={data}
        columns={columns}
        rowKey="_id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 20 }}
      />
    </Box>
  );
};

export default FeedbackPage;
